import React from "react";
import {
  Field as FinalField,
  FieldProps,
  FieldRenderProps
} from "react-final-form";
import { Cell, Column, Columns, Field, Required } from "../../index";
import { Theme, WithStyles } from "@material-ui/core";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import TableBody from "@material-ui/core/TableBody";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableHead from "@material-ui/core/TableHead";
import Add from "@material-ui/icons/Add";
import Delete from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import { FieldArray } from "react-final-form-arrays";

const styles = (theme: Theme) =>
  createStyles({
    card: {
      display: "grid",
      gridTemplateColumns: "100%"
    },
    cardContent: {
      padding: "0",
      overflow: "auto"
    },
    table: {
      tableLayout: "auto"
    },
    cell: {
      overflow: "hidden"
    },
    header: {
      height: "40px",
      paddingBottom: "5px",
      textAlign: "center"
    },
    rowButtons: {
      float: "right",
      color: theme.palette.text.primary
    }
  });

export interface IndexFieldProps
  extends WithStyles<typeof styles>,
    FieldRenderProps<any> {
  columns: Columns;
  row: any;
  key: string;
  readOnly?: boolean;
  values: any;
}

class IndexFieldComponent extends React.Component<IndexFieldProps> {
  private prepareComponentProps = (column: Column, name: string) => {
    return {
      name: name + "." + column.name,
      key: name + "." + column.name,
      disabled: this.props.readOnly ? this.props.readOnly : column.readonly,
      readOnly: this.props.readOnly ? this.props.readOnly : column.readonly,
      validate: column.type.valid,
      values: this.props.values,
      component: column.type.Component,
      ...column.type.props
    } as FieldProps<any, any>;
  };

  private prepareEmptyInput = () => {
    let emptyObj = {};
    this.props.columns.mapFields((field: Field) => {
      emptyObj[field.name] = "";
    });
    return emptyObj;
  };

  private handleAdd = (fields: any) => () => {
    fields.push(this.prepareEmptyInput());
  };

  private handleDelete = (fields: any, idx: number) => () => {
    fields.remove(idx);
  };

  private prepareHeader = (column: Column) => {
    const requiredSuffix =
      column.type.validators.indexOf(Required) > -1 ? " *" : "";
    return (column.title || column.name) + requiredSuffix;
  };

  private getButton = (func: (event: any) => void, icon: JSX.Element) => {
    const { classes } = this.props;

    if (this.props.readOnly) {
      return <></>;
    } else {
      return (
        <Cell key={icon.constructor.name}>
          <IconButton onClick={func} className={classes.rowButtons}>
            {icon}
          </IconButton>
        </Cell>
      );
    }
  };

  public render() {
    const { classes, columns, input } = this.props;

    return (
      <Card variant="outlined" className={classes.card}>
        <CardContent className={classes.cardContent}>
          <FieldArray name={input.name}>
            {({ fields }) => (
              <Table className={classes.table}>
                <TableHead>
                  <TableRow className={classes.header} key={"header"}>
                    {columns.mapFields(column => {
                      return (
                        <Cell key={column.name + "header"}>
                          {this.prepareHeader(column)}
                        </Cell>
                      );
                    })}
                    {this.getButton(this.handleAdd(fields), <Add />)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((name, idx) => (
                    <TableRow key={idx}>
                      {columns.mapFields((column, index) => {
                        const presentationProps = this.prepareComponentProps(
                          column,
                          name
                        );
                        return (
                          <Cell key={name + index} className={classes.cell}>
                            <FinalField
                              {...presentationProps}
                              key={idx + index}
                            />
                          </Cell>
                        );
                      })}
                      {this.getButton(
                        this.handleDelete(fields, idx),
                        <Delete />
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </FieldArray>
        </CardContent>
      </Card>
    );
  }
}

export const IndexViewField = withStyles(styles, { withTheme: true })(
  IndexFieldComponent
);
